import React, { useState, useEffect } from 'react';
import { apiClient } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { 
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue 
} from '../../components/ui/select';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle
} from '../../components/ui/dialog';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow
} from '../../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Award, Search, Eye, XCircle, Building2, Calendar, CheckCircle, AlertTriangle,
  Download, ExternalLink
} from 'lucide-react';
import { toast } from 'sonner';

const STATUS_COLORS = {
  "Ativo": "bg-green-500",
  "Revogado": "bg-red-500",
  "Expirado": "bg-amber-500"
};

export default function GestaCertificadosPage() {
  const [certificados, setCertificados] = useState([]);
  const [instituicoesCertificadas, setInstituicoesCertificadas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCertificado, setSelectedCertificado] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [revogarModalOpen, setRevogarModalOpen] = useState(false);
  const [motivoRevogacao, setMotivoRevogacao] = useState('');
  
  // Filters
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Stats
  const [stats, setStats] = useState({ total: 0, ativos: 0, revogados: 0, expirados: 0 });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [certRes, instRes] = await Promise.all([
        apiClient.get('/certificados-ea'),
        apiClient.get('/instituicoes-certificadas')
      ]);
      
      const certs = certRes.data;
      setCertificados(certs);
      setInstituicoesCertificadas(instRes.data);
      
      // Calculate stats
      const now = new Date();
      const ativos = certs.filter(c => c.status === 'Ativo' && new Date(c.data_validade) > now).length;
      const revogados = certs.filter(c => c.status === 'Revogado').length;
      const expirados = certs.filter(c => c.status === 'Ativo' && new Date(c.data_validade) <= now).length;
      
      setStats({
        total: certs.length,
        ativos,
        revogados,
        expirados
      });
    } catch (error) {
      console.error('Error:', error);
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleRevogar = async () => {
    if (!selectedCertificado) return;
    
    try {
      await apiClient.put(`/certificados-ea/${selectedCertificado.id}/revogar?motivo=${encodeURIComponent(motivoRevogacao)}`);
      toast.success('Certificado revogado com sucesso');
      setRevogarModalOpen(false);
      setMotivoRevogacao('');
      loadData();
    } catch (error) {
      toast.error('Erro ao revogar certificado');
    }
  };

  const handleDownloadPdf = async (certificadoId) => {
    try {
      toast.info('A gerar PDF...');
      const response = await apiClient.get(`/certificados-ea/${certificadoId}/pdf`, {
        responseType: 'blob'
      });
      
      // Create download link
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Certificado_EA_${certificadoId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('PDF descarregado com sucesso');
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast.error('Erro ao descarregar PDF');
    }
  };

  const openDetailModal = (cert) => {
    setSelectedCertificado(cert);
    setDetailModalOpen(true);
  };

  const openRevogarModal = (cert) => {
    setSelectedCertificado(cert);
    setRevogarModalOpen(true);
  };

  const isExpired = (validade) => {
    return new Date(validade) <= new Date();
  };

  const getStatusDisplay = (cert) => {
    if (cert.status === 'Revogado') return 'Revogado';
    if (isExpired(cert.data_validade)) return 'Expirado';
    return 'Ativo';
  };

  const filteredCertificados = certificados.filter(c => {
    const status = getStatusDisplay(c);
    const matchesStatus = filterStatus === 'all' || status === filterStatus;
    const matchesSearch = !searchTerm || 
      c.instituicao_nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      c.id?.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="w-8 h-8 border-2 border-slate-200 border-t-[#0066CC] rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="gestao-certificados-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 dark:text-white">Certificados EA</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Gestão de certificações de instituições</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Total Emitidos</p>
                <p className="text-3xl font-bold text-slate-900 dark:text-white">{stats.total}</p>
              </div>
              <Award className="w-10 h-10 text-[#0066CC] opacity-30" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Ativos</p>
                <p className="text-3xl font-bold text-green-600">{stats.ativos}</p>
              </div>
              <CheckCircle className="w-10 h-10 text-green-500 opacity-30" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Expirados</p>
                <p className="text-3xl font-bold text-amber-600">{stats.expirados}</p>
              </div>
              <AlertTriangle className="w-10 h-10 text-amber-500 opacity-30" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-slate-500">Revogados</p>
                <p className="text-3xl font-bold text-red-600">{stats.revogados}</p>
              </div>
              <XCircle className="w-10 h-10 text-red-500 opacity-30" />
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="certificados">
        <TabsList>
          <TabsTrigger value="certificados">Certificados</TabsTrigger>
          <TabsTrigger value="instituicoes">Instituições Certificadas</TabsTrigger>
        </TabsList>

        <TabsContent value="certificados" className="space-y-4">
          {/* Filters */}
          <Card>
            <CardContent className="pt-4">
              <div className="flex flex-wrap gap-4">
                <div className="relative flex-1 min-w-[200px]">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    placeholder="Pesquisar por instituição ou ID..."
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Status" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="Ativo">Ativos</SelectItem>
                    <SelectItem value="Expirado">Expirados</SelectItem>
                    <SelectItem value="Revogado">Revogados</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </CardContent>
          </Card>

          {/* Table */}
          <Card>
            <CardContent className="pt-4">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>ID Certificado</TableHead>
                    <TableHead>Instituição</TableHead>
                    <TableHead className="text-center">Score</TableHead>
                    <TableHead>Data Emissão</TableHead>
                    <TableHead>Validade</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Ações</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredCertificados.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center py-12 text-slate-500">
                        Nenhum certificado encontrado
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredCertificados.map(cert => {
                      const status = getStatusDisplay(cert);
                      return (
                        <TableRow key={cert.id}>
                          <TableCell>
                            <span className="font-mono text-sm">{cert.id}</span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <Building2 className="w-4 h-4 text-slate-400" />
                              <span className="font-medium">{cert.instituicao_nome}</span>
                            </div>
                          </TableCell>
                          <TableCell className="text-center">
                            <span className="font-bold text-[#0066CC]">{cert.score}</span>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1 text-sm">
                              <Calendar className="w-3 h-3 text-slate-400" />
                              {new Date(cert.data_emissao).toLocaleDateString('pt-PT')}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className={`flex items-center gap-1 text-sm ${isExpired(cert.data_validade) ? 'text-red-600' : ''}`}>
                              <Calendar className="w-3 h-3" />
                              {new Date(cert.data_validade).toLocaleDateString('pt-PT')}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge className={`${STATUS_COLORS[status]} text-white`}>
                              {status}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-right">
                            <div className="flex justify-end gap-1">
                              <Button variant="ghost" size="sm" onClick={() => openDetailModal(cert)} title="Ver detalhes">
                                <Eye className="w-4 h-4" />
                              </Button>
                              <Button variant="ghost" size="sm" onClick={() => handleDownloadPdf(cert.id)} title="Descarregar PDF" className="text-amber-600">
                                <Download className="w-4 h-4" />
                              </Button>
                              {status === 'Ativo' && (
                                <Button variant="ghost" size="sm" onClick={() => openRevogarModal(cert)} className="text-red-600" title="Revogar">
                                  <XCircle className="w-4 h-4" />
                                </Button>
                              )}
                            </div>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="instituicoes" className="space-y-4">
          <Card>
            <CardContent className="pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {instituicoesCertificadas.length === 0 ? (
                  <div className="col-span-full text-center py-12 text-slate-500">
                    <Award className="w-12 h-12 mx-auto mb-4 opacity-30" />
                    <p>Nenhuma instituição certificada</p>
                  </div>
                ) : (
                  instituicoesCertificadas.map(inst => (
                    <Card key={inst.id} className="overflow-hidden">
                      <div className="h-2 bg-gradient-to-r from-amber-500 to-yellow-400"></div>
                      <CardContent className="pt-4">
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <p className="font-bold text-lg">{inst.nome}</p>
                            <p className="text-sm text-slate-500">{inst.distrito_nome || inst.tipo_entidade}</p>
                          </div>
                          <Award className="w-8 h-8 text-amber-500" />
                        </div>
                        <div className="grid grid-cols-2 gap-3 text-sm">
                          <div>
                            <p className="text-slate-500">Score</p>
                            <p className="font-bold text-[#0066CC]">{inst.ultimo_score_avaliacao}</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Classificação</p>
                            <p className="font-bold">{inst.ultima_classificacao}</p>
                          </div>
                        </div>
                        <div className="mt-3 pt-3 border-t text-xs text-slate-500">
                          <p>Certificado: {inst.certificado_ea_id}</p>
                          <p>Válido até: {new Date(inst.validade_certificacao).toLocaleDateString('pt-PT')}</p>
                        </div>
                      </CardContent>
                    </Card>
                  ))
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Detail Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Detalhes do Certificado</DialogTitle>
          </DialogHeader>
          {selectedCertificado && (
            <div className="space-y-4 py-4">
              <div className="p-4 bg-gradient-to-r from-amber-50 to-yellow-50 dark:from-amber-900/20 dark:to-yellow-900/20 rounded-lg text-center">
                <Award className="w-12 h-12 mx-auto text-amber-500 mb-2" />
                <p className="font-mono text-lg font-bold">{selectedCertificado.id}</p>
                <Badge className={`${STATUS_COLORS[getStatusDisplay(selectedCertificado)]} text-white mt-2`}>
                  {getStatusDisplay(selectedCertificado)}
                </Badge>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <p className="text-xs text-slate-500">Instituição</p>
                  <p className="font-medium">{selectedCertificado.instituicao_nome}</p>
                </div>
                <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <p className="text-xs text-slate-500">Score</p>
                  <p className="font-bold text-[#0066CC] text-xl">{selectedCertificado.score}</p>
                </div>
                <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <p className="text-xs text-slate-500">Data de Emissão</p>
                  <p className="font-medium">{new Date(selectedCertificado.data_emissao).toLocaleDateString('pt-PT')}</p>
                </div>
                <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <p className="text-xs text-slate-500">Data de Validade</p>
                  <p className={`font-medium ${isExpired(selectedCertificado.data_validade) ? 'text-red-600' : ''}`}>
                    {new Date(selectedCertificado.data_validade).toLocaleDateString('pt-PT')}
                  </p>
                </div>
              </div>

              {selectedCertificado.emitido_por_nome && (
                <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                  <p className="text-xs text-slate-500">Emitido por</p>
                  <p className="font-medium">{selectedCertificado.emitido_por_nome}</p>
                </div>
              )}

              {selectedCertificado.status === 'Revogado' && (
                <div className="p-3 bg-red-50 dark:bg-red-900/20 rounded-lg border border-red-200">
                  <p className="text-xs text-red-600">Motivo da Revogação</p>
                  <p className="text-sm">{selectedCertificado.motivo_revogacao || 'Não especificado'}</p>
                  <p className="text-xs text-red-500 mt-1">
                    Revogado em: {new Date(selectedCertificado.data_revogacao).toLocaleDateString('pt-PT')}
                  </p>
                </div>
              )}

              <Button 
                onClick={() => handleDownloadPdf(selectedCertificado.id)} 
                className="w-full bg-amber-600 hover:bg-amber-700"
              >
                <Download className="w-4 h-4 mr-2" />
                Descarregar Certificado PDF
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Revogar Modal */}
      <Dialog open={revogarModalOpen} onOpenChange={setRevogarModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Revogar Certificado</DialogTitle>
            <DialogDescription>
              Esta ação é irreversível. O certificado será marcado como revogado.
            </DialogDescription>
          </DialogHeader>
          {selectedCertificado && (
            <div className="py-4 space-y-4">
              <div className="p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
                <p className="text-xs text-slate-500">Certificado</p>
                <p className="font-mono font-bold">{selectedCertificado.id}</p>
                <p className="text-sm text-slate-600">{selectedCertificado.instituicao_nome}</p>
              </div>
              
              <div>
                <Label>Motivo da Revogação</Label>
                <Textarea
                  value={motivoRevogacao}
                  onChange={e => setMotivoRevogacao(e.target.value)}
                  placeholder="Indique o motivo..."
                  rows={3}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setRevogarModalOpen(false)}>Cancelar</Button>
            <Button onClick={handleRevogar} className="bg-red-600 hover:bg-red-700">
              <XCircle className="w-4 h-4 mr-2" />
              Revogar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
